import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

import { useCreateSale } from "../../../hooks/useSale";

// Todo: Validation for if year exists, or if year is in the past.

// Todo: interpert error messages for user freindly responses

const cancelIcon = <FontAwesomeIcon icon={faXmark} />
const checkIcon = <FontAwesomeIcon icon={faCheck} />

// Sale hours are Waitsfield wall-clock time, so dates are always interpreted
// in Eastern regardless of where the admin happens to be. Posting a bare date
// string would be parsed by the API as UTC midnight, which silently shifts
// every sale date by the Eastern offset.
export const SALE_ZONE = 'America/New_York'

// Combine a 'YYYY-MM-DD' date and 'HH:mm' time in SALE_ZONE. Luxon quietly
// moves a time that falls in a daylight-saving gap forward by an hour, so the
// result is checked against the requested wall-clock time and marked invalid
// if it no longer matches.
const toSaleDateTime = (date, time) => {
  const dt = DateTime.fromISO(`${date}T${time}`, { zone: SALE_ZONE })
  if (!dt.isValid || dt.toFormat('HH:mm') !== time) {
    return DateTime.invalid('does not exist in sale zone')
  }
  return dt
}

const DateTimeField = ({ id, name, date, time, onDateChange, onTimeChange }) => (
  <div className="field">
    <label className="label" htmlFor={`${id}-date`}>{`${name} Date`}</label>
    <div className="field is-grouped">
      <div className="control is-expanded">
        <input id={`${id}-date`} className="input" type="date" value={date}
          onChange={e => onDateChange(e.target.value)} />
      </div>
      <div className="control">
        <input id={`${id}-time`} className="input" type="time" value={time}
          aria-label={`${name} Time`}
          onChange={e => onTimeChange(e.target.value)} />
      </div>
    </div>
  </div>
)

export default function SaleAdd() {

  const today = DateTime.local().toISODate()
  const [startDate, setStartDate] = useState(today)
  const [startTime, setStartTime] = useState('09:00')
  const [endDate, setEndDate] = useState(today)
  const [endTime, setEndTime] = useState('16:00')
  const [consignmentStartDate, setConsignmentStartDate] = useState(today)
  const [consignmentStartTime, setConsignmentStartTime] = useState('00:00')
  const [consignmentEndDate, setConsignmentEndDate] = useState(today)
  const [consignmentEndTime, setConsignmentEndTime] = useState('23:00')
  const [error, setError] = useState(null)
  const createSale = useCreateSale()
  const navigate = useNavigate()

  const save = () => {
    const fields = [
      ['Start', toSaleDateTime(startDate, startTime)],
      ['End', toSaleDateTime(endDate, endTime)],
      ['Consignment Start', toSaleDateTime(consignmentStartDate, consignmentStartTime)],
      ['Consignment End', toSaleDateTime(consignmentEndDate, consignmentEndTime)]
    ]
    const invalid = fields.find(([, dt]) => !dt.isValid)
    if (invalid) {
      setError(`${invalid[0]} is not a valid date and time.`)
      return
    }
    const [saleStart, saleEnd, consignmentStart, consignmentEnd] = fields.map(([, dt]) => dt)
    if (saleEnd <= saleStart) {
      setError('End Date must be after Start Date.')
      return
    }
    if (consignmentEnd <= consignmentStart) {
      setError('Consignment End must be after Consignment Start.')
      return
    }

    setError(null)
    createSale.mutateAsync({
      sale_start: saleStart.toISO(),
      sale_end: saleEnd.toISO(),
      consignment_start: consignmentStart.toISO(),
      consignment_end: consignmentEnd.toISO()
    })
    .then(res => navigate('/admin/sales'))
    .catch(err => setError(err?.message || 'Unable to save the sale.'))
  }

  return (
    <div className="form has-text-left">
      <div className="title">Add Sale</div>
      {error &&
        <div className="notification is-danger" role="alert">
          {error}
        </div>
      }
      <p className="help mb-3">All times are US Eastern (Waitsfield).</p>
      <DateTimeField id="sale-start" name="Start"
        date={startDate} time={startTime}
        onDateChange={setStartDate} onTimeChange={setStartTime} />
      <DateTimeField id="sale-end" name="End"
        date={endDate} time={endTime}
        onDateChange={setEndDate} onTimeChange={setEndTime} />
      <DateTimeField id="consignment-start" name="Consignment Start"
        date={consignmentStartDate} time={consignmentStartTime}
        onDateChange={setConsignmentStartDate} onTimeChange={setConsignmentStartTime} />
      <DateTimeField id="consignment-end" name="Consignment End"
        date={consignmentEndDate} time={consignmentEndTime}
        onDateChange={setConsignmentEndDate} onTimeChange={setConsignmentEndTime} />
      <div className="buttons is-grouped">
        <button className="button is-link" onClick={save} disabled={createSale.isLoading}>
          <span className="icon">
            <i>{checkIcon}</i>
          </span>
          <span>Save</span>
        </button>
        <button className="button is-info" onClick={() => navigate('/admin/sales')}>
          <span className="icon">
            <i>{cancelIcon}</i>
          </span>
          <span>Cancel</span>
        </button>
      </div>
    </div>
  )
}
